import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageSquare,
  ClipboardList,
  Activity,
  BarChart3,
  FileText,
  CheckCircle,
  Clock,
  Shield,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { howItWorksSteps, images } from "../data/mock";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { usePageSEO } from "../hooks/usePageSEO";

const stepIcons = [
  MessageSquare,
  ClipboardList,
  Activity,
  BarChart3,
  FileText,
];

const expectations = [
  {
    icon: Clock,
    title: "Duration",
    description: "The complete process takes 90–120 minutes from start to finish.",
  },
  {
    icon: Shield,
    title: "Privacy",
    description: "Everything discussed and recorded is strictly confidential.",
  },
  {
    icon: CheckCircle,
    title: "Comfort",
    description: "The test is non-invasive and conducted in a comfortable setting.",
  },
];

export default function HowItWorksPage() {
  const { howItWorks: howItWorksSteps, images } = useSiteConfig();
  
  usePageSEO({
    title: "How It Works",
    description: "Learn about the polygraph examination process - from initial consultation to results delivery. Understand what to expect during your lie detector test.",
    path: "/how-it-works"
  });
  
  return (
    <main>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0F]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC2626]/[0.03] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            The Process
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            How Polygraph
            <br />
            <span className="text-[#DC2626]">Testing Works</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
            Understanding the process helps put examinees at ease. Here's a
            step-by-step guide to what you can expect during a polygraph
            examination.
          </p>
        </div>
      </section>

      {/* Steps Timeline */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 lg:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#DC2626]/40 via-[#DC2626]/20 to-transparent hidden md:block" />

            <div className="space-y-12 lg:space-y-16">
              {howItWorksSteps.map((step, i) => {
                const Icon = stepIcons[i] || FileText;
                return (
                  <div key={i} className="relative group">
                    <div className="flex gap-6 lg:gap-10">
                      {/* Step number/icon */}
                      <div className="relative shrink-0 hidden md:block">
                        <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl bg-[#111118] border border-white/[0.06] flex items-center justify-center z-10 relative group-hover:border-[#DC2626]/30 transition-colors duration-300">
                          <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-[#DC2626]" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 bg-[#111118] border border-white/[0.04] rounded-2xl p-8 lg:p-10 group-hover:border-[#DC2626]/15 transition-all duration-500">
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-[#DC2626] font-display text-3xl lg:text-4xl font-bold opacity-40">
                            {step.number}
                          </span>
                          <h3 className="text-white font-semibold text-xl lg:text-2xl">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-gray-400 leading-relaxed lg:text-base">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="bg-[#0D0D14] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div>
              <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
                What to Expect
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                A Professional, Comfortable Experience
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                We understand that undergoing a polygraph examination can feel
                daunting. Our process is designed to be transparent, respectful,
                and as comfortable as possible.
              </p>
              <div className="space-y-6">
                {expectations.map((item, i) => {
                  const ExpIcon = item.icon;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center shrink-0">
                        <ExpIcon className="h-5 w-5 text-[#DC2626]" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm mb-1">
                          {item.title}
                        </h4>
                        <p className="text-gray-500 text-sm leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-[#DC2626]/[0.02] rounded-3xl blur-3xl" />
              <img
                src={images.magnifyingGlass}
                alt="Investigation"
                className="relative rounded-2xl w-full h-[420px] object-cover shadow-2xl shadow-black/50"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0D0D14]/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0F] py-24 lg:py-28 border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Have questions about the process? Contact us for a free, no-obligation
            consultation. We're happy to explain everything in detail.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium transition-colors duration-200"
            >
              Book a Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
